import { resolveEffectiveAnswerForSubitems } from './unansweredValue';
import m0150Aliases from './m0150Aliases.json';
import { Question, OASISSchema, OASISFormData, FormValue } from './types';

/**
 * Checks if a value is meaningful (not empty).
 */
const hasValue = (value: FormValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  return true;
};

const M0150_LABELS: Record<string, string> = m0150Aliases.enhancedFormLabels;

/**
 * M0150 answered only under the retired intake id counts as answered under the
 * Enhanced question this form asks, its labels spelled as that form offers them.
 * The retired answer is used only when neither current id holds an answer.
 * Mirrors `apply_replacements` in the backend; a backend test pins the table.
 */
const applyM0150Alias = (result: OASISFormData, schema: OASISSchema) => {
  const asked = new Set<string>();
  const collect = (question: Question) => {
    if (m0150Aliases.enhancedQids.includes(question.qid)) {
      asked.add(question.qid);
    }
    Object.values(question.subitems ?? {}).forEach(items =>
      items.forEach(collect)
    );
  };
  Object.values(schema).forEach(questions => questions.forEach(collect));

  const spell = (value: FormValue | undefined) =>
    Array.isArray(value)
      ? value.map(label => M0150_LABELS[label as string] ?? label)
      : value;
  const retired = result[m0150Aliases.retiredQid];
  const currentAnswered = m0150Aliases.enhancedQids.some(qid =>
    hasValue(result[qid])
  );
  asked.forEach(qid => {
    const source = hasValue(result[qid])
      ? result[qid]
      : currentAnswered
        ? undefined
        : retired;
    if (hasValue(source)) result[qid] = spell(source) as FormValue;
  });
};

/**
 * Answers with each replacing question filled from the question it replaces,
 * when the visit was answered before the change. A stored answer to the newer
 * question always wins, and the older answer is left as it is.
 */
export const applyReplacements = (
  formData: OASISFormData,
  schema: OASISSchema
): OASISFormData => {
  const result: OASISFormData = { ...formData };
  const visit = (question: Question) => {
    const rule = question.replaces;
    if (rule && !hasValue(result[question.qid])) {
      const old = result[rule.qid];
      const mapped = typeof old === 'string' ? rule.values[old] : undefined;
      if (mapped !== undefined) result[question.qid] = mapped;
    }
    // A frozen layout may still ask the retired question; answer it from the
    // successor, as the PDF does, but never over a stored retired answer.
    if (rule?.question && !hasValue(result[rule.qid])) {
      const answer = result[question.qid];
      const old = Object.keys(rule.values).find(k => rule.values[k] === answer);
      if (old !== undefined) result[rule.qid] = old;
    }
    Object.values(question.subitems ?? {}).forEach(items =>
      items.forEach(visit)
    );
  };
  Object.values(schema).forEach(questions => questions.forEach(visit));
  applyM0150Alias(result, schema);
  return result;
};

/**
 * Returns subitem questions revealed by the parent's current answer value.
 * Matches the subitem visibility rules used by `OASISQuestionRenderer`.
 */
export const getSelectedSubitems = (
  question: Question,
  value: FormValue | undefined
): Question[] => {
  if (!question.subitems) return [];

  const effectiveValue = resolveEffectiveAnswerForSubitems(question, value);
  const selectedValues =
    typeof effectiveValue === 'string'
      ? [effectiveValue]
      : Array.isArray(effectiveValue)
        ? effectiveValue.filter(
            (item): item is string => typeof item === 'string'
          )
        : [];

  const seenQids = new Set<string>();
  const selectedSubitems: Question[] = [];

  selectedValues.forEach(selectedValue => {
    (question.subitems?.[selectedValue] ?? []).forEach(subitem => {
      if (seenQids.has(subitem.qid)) return;
      seenQids.add(subitem.qid);
      selectedSubitems.push(subitem);
    });
  });

  return selectedSubitems;
};

/**
 * Extracts OASIS form data for persistence or validation.
 * Returns sparse QID-based data: only questions with meaningful answers.
 *
 * @param formData The complete form data
 * @param schema The OASIS schema containing all questions
 * @returns Form data with QIDs as keys
 */
export const getOASISFormData = (
  formData: OASISFormData,
  schema: OASISSchema
): OASISFormData => {
  const result: OASISFormData = {};

  // Process all questions recursively
  const processQuestion = (question: Question) => {
    const value = formData[question.qid];

    if (hasValue(value)) {
      result[question.qid] = value;
      // Also save the replaced question's answer, so a form still on the older
      // schema sees its question answered and keeps the rows under it.
      const rule = question.replaces;
      const old = rule
        ? Object.keys(rule.values).find(key => rule.values[key] === value)
        : undefined;
      if (rule && old !== undefined) result[rule.qid] = old;
    }

    // Process subitems for the currently selected value
    if (question.subitems) {
      getSelectedSubitems(question, value).forEach(subitem =>
        processQuestion(subitem)
      );
    }
  };

  // Process all categories
  Object.values(schema).forEach(questions => {
    questions.forEach(question => processQuestion(question));
  });

  return result;
};

/**
 * Clears subitem data when a parent question's value changes.
 * Returns an object with subitem keys to be cleared (set to undefined).
 *
 * @param question The parent question that changed
 * @param newValue The new value selected for the parent question
 * @param currentFormData Current form data to check for existing subitem values
 * @returns Object with subitem keys that should be cleared
 */
export const getSubitemClearUpdates = (
  question: Question,
  newValue: FormValue,
  currentFormData: OASISFormData
): Partial<OASISFormData> => {
  const clearUpdates: Partial<OASISFormData> = {};

  if (!question.subitems) {
    return clearUpdates;
  }

  // Collect all subitem QIDs recursively
  const collectAllQids = (q: Question, qids: Set<string>) => {
    if (q.subitems) {
      Object.values(q.subitems).forEach(items => {
        items.forEach(item => {
          qids.add(item.qid);
          collectAllQids(item, qids);
        });
      });
    }
  };

  // Get all possible subitem QIDs
  const allQids = new Set<string>();
  collectAllQids(question, allQids);

  // Get QIDs that should remain active (for the new value)
  const activeQids = new Set<string>();
  getSelectedSubitems(question, newValue).forEach(subitem => {
    activeQids.add(subitem.qid);
    collectAllQids(subitem, activeQids);
  });

  // Clear inactive subitems that have values
  allQids.forEach(qid => {
    if (!activeQids.has(qid) && currentFormData[qid] !== undefined) {
      clearUpdates[qid] = undefined;
    }
  });

  return clearUpdates;
};
